import { MenuAction, UserPermission } from "@archetypes/app";
import { Position } from "@archetypes/form";
import {
  AppMenuSchemaType,
  AppPermissionSchemaType,
} from "@dialogs/app-add-edit-dialog/app-add-edit-form.schema";

export function getPermissionType(permissions: AppPermissionSchemaType) {
  return permissions.allAdmins
    ? UserPermission.ALL_ADMIN
    : permissions.allUsers
    ? UserPermission.ALL_USERS
    : permissions?.specificGroups?.length
    ? UserPermission.SPECIFIC_GROUPS
    : UserPermission.SPECIFIC_USERS;
}

export function getPermissionPayload({
  permissions,
  permissionType,
}: {
  permissions?: AppPermissionSchemaType;
  permissionType?: UserPermission;
}): AppPermissionSchemaType {
  return {
    allAdmins: permissionType === UserPermission.ALL_ADMIN,
    allUsers: permissionType === UserPermission.ALL_USERS,
    specificGroups:
      permissionType === UserPermission.SPECIFIC_GROUPS ? permissions?.specificGroups ?? [] : [],
    specificUsers:
      permissionType === UserPermission.SPECIFIC_USERS ? permissions?.specificUsers ?? [] : [],
  };
}
export const getLinkFromMenuParams = (baseRoute: string, menu: AppMenuSchemaType) => {
  if (!menu._id) {
    return { path: baseRoute, isExternalLink: false };
  }
  if (menu.callAnApi) {
    return { path: `${baseRoute}/api-call?menuId=${menu._id}`, isExternalLink: false };
  }
  if (menu.params?.link) {
    return { path: `${menu.params.link}`, isExternalLink: true };
  }

  if (menu.params?.formId) {
    let route = `${baseRoute}/forms/${menu.params.formId}`;
    if (menu.params.templateId) {
      route += `/responses/${menu.params.templateId}`;
    }
    return { path: `${route}?menuId=${menu._id}`, isExternalLink: false };
  }
  if (menu.params?.dashboardId) {
    return {
      path: `${baseRoute}/dashboards/${menu.params.dashboardId}?menuId=${menu._id}`,
      isExternalLink: false,
    };
  }
  return { path: `${baseRoute}?menuId=${menu._id}`, isExternalLink: false };
};

export const isValidSubMenu = (menu: AppMenuSchemaType) => {
  return (
    Array.isArray(menu?.children) &&
    menu?.children?.length > 0 &&
    menu?.action === MenuAction.OPEN_SUB_MENU
  );
};

export function getActiveMenuAndExpandedSubMenus(
  menuMap: Record<string, AppMenuSchemaType> = {},
  id: string,
) {
  for (const menuId in menuMap) {
    if (!Object.hasOwn(menuMap, menuId)) continue;
    const menu = menuMap[menuId];
    if (id === menuId) {
      const expandedSubMenus: Record<string, boolean> = {};
      if (Array.isArray(menu.parentTree) && menu.parentTree.length > 0) {
        menu.parentTree.forEach((id) => (expandedSubMenus[id] = true));
      }
      return { activeMenu: menu._id as string, expandedSubMenus };
    }
  }
}

export function buildMenuMap(
  menuData: AppMenuSchemaType[],
  menuMap: Record<string, AppMenuSchemaType>,
): Record<string, AppMenuSchemaType> {
  let newMenuMap = { ...menuMap };
  for (const menu of menuData) {
    newMenuMap[menu?._id as string] = { ...menu, children: [] };
    if (menu?.children && menu?.children?.length) {
      newMenuMap = buildMenuMap(menu?.children as AppMenuSchemaType[], newMenuMap);
    }
  }
  return newMenuMap;
}

export const deleteAppMenu = ({
  menus,
  menuId,
}: {
  menus: AppMenuSchemaType[];
  menuId: string;
}): AppMenuSchemaType[] => {
  return menus.filter((menu) => {
    if (menu._id === menuId) return false;
    if (menu.children && menu.children.length > 0) {
      menu.children = deleteAppMenu({ menus: menu.children, menuId });
    }
    return true;
  });
};

export const findAndUpdateAppMenu = ({
  menus,
  updatedMenu,
}: {
  menus: AppMenuSchemaType[];
  updatedMenu: AppMenuSchemaType;
}): AppMenuSchemaType[] => {
  return menus.map((menu) => {
    if (menu._id === updatedMenu._id) {
      return updatedMenu;
    }
    if (menu.children && menu.children.length > 0) {
      menu.children = findAndUpdateAppMenu({ menus: menu.children, updatedMenu });
    }
    return menu;
  });
};

export const getAppMenuById = ({
  menus,
  menuId,
}: {
  menus: AppMenuSchemaType[];
  menuId?: string;
}): AppMenuSchemaType | undefined => {
  return menus.find((menu) => {
    if (menu._id === menuId) {
      return true;
    }
    if (menu.children && menu.children.length > 0) {
      return !!getAppMenuById({ menus: menu.children, menuId });
    }
    return false;
  });
};

export const getHomePageMenus = ({
  menus,
  homePageMenus,
}: {
  menus: AppMenuSchemaType[];
  homePageMenus: AppMenuSchemaType[];
}): AppMenuSchemaType[] => {
  menus.forEach((menu) => {
    if (
      menu.action === MenuAction.OPEN_FORM ||
      menu.action === MenuAction.SHOW_RESPONSES ||
      menu.action === MenuAction.OPEN_DASHBOARD
    ) {
      homePageMenus.push(menu);
    }
    if (menu.children && menu.children.length > 0) {
      homePageMenus.concat(getHomePageMenus({ menus: menu.children, homePageMenus }));
    }
  });
  return homePageMenus;
};

export const moveAppMenu = ({
  menus,
  currentMenu,
  position,
  targetId,
}: {
  menus: AppMenuSchemaType[];
  currentMenu: AppMenuSchemaType;
  position: Position;
  targetId?: string;
}): AppMenuSchemaType[] => {
  const newTree = deleteAppMenu({ menus, menuId: currentMenu._id as string });

  if (!targetId) {
    // For a new / existing node with no parent
    return newTree.concat({ ...currentMenu, parentTree: [], parentMenuId: undefined });
  }

  const insertAppMenu = (menus: AppMenuSchemaType[]): AppMenuSchemaType[] => {
    return menus
      .map((menu) => {
        if (menu._id === targetId) {
          if (position === Position.WITHIN) {
            const updatedNode = {
              ...currentMenu,
              parentTree: [...(menu.parentTree ?? []), menu._id],
              parentMenuId: menu._id,
            };
            menu.children = menu.children ? [...menu.children, updatedNode] : [updatedNode];
          } else {
            const updatedNode = {
              ...currentMenu,
              parentTree: menu.parentTree,
              parentMenuId: menu.parentMenuId,
            };
            return position === Position.BEFORE ? [updatedNode, menu] : [menu, updatedNode];
          }
        }
        if (menu.children && menu.children.length > 0) {
          menu.children = insertAppMenu(menu.children);
        }
        return menu;
      })
      .flat();
  };

  return insertAppMenu(newTree);
};

export const getParentableAppMenus = ({
  menus,
  menuId,
  parents,
}: {
  menus: AppMenuSchemaType[];
  menuId?: string;
  parents: AppMenuSchemaType[];
}) => {
  menus.forEach((menu) => {
    if (
      menu._id === menuId || // A menu cannot be its own parent
      (menuId && menu.parentTree && menu.parentTree.includes(menuId)) || // A menu's child cannot be its own parent
      menu.action !== MenuAction.OPEN_SUB_MENU || // Only a sub-menu can be a parent
      (menu.parentTree && menu.parentTree.length > 2) // We don't allow creation of sub-menus below 2nd level
    ) {
      return;
    }

    parents.push(menu);
    if (menu.children) {
      parents.concat(getParentableAppMenus({ menus: menu.children, menuId, parents }));
    }
  });
  return parents;
};

export const getMovableMenus = ({
  menus,
  position,
  menuId,
  parents,
}: {
  menus: AppMenuSchemaType[];
  position: Position;
  menuId: string;
  parents: AppMenuSchemaType[];
}) => {
  menus.forEach((menu) => {
    const immovable =
      menu._id === menuId || // A menu cannot be moved before/after/within itself
      (menuId && menu.parentTree && menu.parentTree.includes(menuId)); //  A menu cannot be moved before/after/within its children
    if ((position === Position.AFTER || position === Position.BEFORE) && immovable) {
      return;
    } else if (
      position === Position.WITHIN &&
      (immovable ||
        menu.action !== MenuAction.OPEN_SUB_MENU || //  A menu can only be moved within another sub-menu
        (menu.parentTree && menu.parentTree.length > 2)) //  A menu cannot be moved within a sub-menu after 2nd level
    ) {
      return;
    }

    parents.push(menu);
    if (menu.children) {
      parents.concat(getMovableMenus({ menus: menu.children, position, menuId, parents }));
    }
  });
  return parents;
};

export const getAppMenuHierarchyTitle = ({
  menuMap,
  menuId,
}: {
  menuMap: Record<string, AppMenuSchemaType>;
  menuId: string;
}) => {
  const menu = menuMap[menuId];
  let hierarchyTitle = "";
  if (menu) {
    const parentIds = menu?.parentTree ?? [];
    for (const parentId of parentIds) {
      hierarchyTitle += menuMap[parentId].title + " > ";
    }
    hierarchyTitle += menu.title;
  }
  return hierarchyTitle;
};

/**
 * Converts a string array into an object of cumulative paths.
 */
export function convertToPathObject(items: string[]): Record<string, true> {
  const result: Record<string, true> = {};
  let currentPath = "";

  items.forEach((item, index) => {
    // Append the current item to the path
    // If it's the first item, don't prepend a slash
    currentPath = index === 0 ? item : `${currentPath}/${item}`;

    // Assign to the object
    result[currentPath] = true;
  });

  return result;
}
